import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { useAuthUser } from '@/hooks/useAuthUser';
import { v1 } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { assetInstanceToLegacy } from '@/infrastructure/api/v1/mappers/assetInstances';
import type { AnyCropData } from '@/modules/uiImages/cropUtils';
import { isAnchorThumbnail } from '@/modules/uiImages/scanImages';
import { TEMPLATE_COVER_ORDER } from '@/modules/uiImages/types';
import type { UIImage, UIImageCategory } from '@/modules/uiImages/types';
import { IMAGE_LIST_FAILED, pickDisplayedImage } from '@/modules/uiImages/pickDisplayedImage';
import type { AnchorCategory } from './types';

export type CoverCategory = 'ProjectPortrait' | 'ProjectLandscape' | 'SceneLandscape';

export interface CoverEntry {
  id: string | null;
  url: string | null;
  originalUrl: string | null;
  crop: AnyCropData | null;
}

export interface CoverSavePayload {
  imageOriginalUrl: string;
  imageCroppedUrl?: string;
  crop?: AnyCropData;
}

export interface TemplateLandscapeEntry extends CoverEntry {
  imageId: string;
}

export interface PublishFlowImagesApi {
  anchorCategory: AnchorCategory | null;
  anchorLatitude: number | null;
  anchorLongitude: number | null;
  projectPortrait: CoverEntry;
  projectLandscape: CoverEntry;
  sceneLandscape: CoverEntry;
  scanImage: CoverEntry;
  scanImageUrl: string | null;
  // True while the scan image still shows an anchor's thumbnail, not one the creator supplied
  scanImageIsAutoThumbnail: boolean;
  imageAnchorUrl: string | null;
  templateVideoUrl: string | null;
  // What the gallery shows as the template's cover: its own image when it has one,
  // otherwise the story's.
  templateCover: CoverEntry;
  // The template's own cover row, null while it is inheriting the story's.
  templateCoverOwn: TemplateLandscapeEntry | null;
  templateLandscapes: TemplateLandscapeEntry[];
  saveImage: (category: CoverCategory, payload: CoverSavePayload) => Promise<void>;
  removeImage: (category: CoverCategory) => Promise<void>;
  saveScanImage: (payload: CoverSavePayload) => Promise<void>;
  removeScanImage: () => Promise<void>;
  saveTemplateVideo: (videoUrl: string) => Promise<void>;
  removeTemplateVideo: () => Promise<void>;
  saveTemplateCover: (payload: CoverSavePayload) => Promise<void>;
  removeTemplateCover: () => Promise<void>;
  saveTemplateLandscape: (imageId: string | null, payload: CoverSavePayload) => Promise<void>;
  removeTemplateLandscape: (imageId: string) => Promise<void>;
}

const EMPTY_ENTRY: CoverEntry = { id: null, url: null, originalUrl: null, crop: null };

function firstOfCategory(images: UIImage[], category: UIImageCategory): UIImage | undefined {
  return pickDisplayedImage(images, category) ?? undefined;
}

function toEntry(image: UIImage | undefined): CoverEntry {
  if (!image) return EMPTY_ENTRY;
  return {
    id: image.imageId ?? null,
    url: image.imageCroppedUrl || image.imageOriginalUrl || null,
    originalUrl: image.imageOriginalUrl || null,
    crop: (image.imageMetadata?.crop as AnyCropData) ?? null,
  };
}

function pickUrl(images: UIImage[], category: UIImageCategory): string | null {
  return toEntry(firstOfCategory(images, category)).url;
}

// The story's scan image, as the story page and phones pick it: the newest with a url (rows come newest first)
function newestScan(images: UIImage[]): UIImage | undefined {
  return images.find((img) => img.imageCategory === 'ScanImage' && !!(img.imageCroppedUrl || img.imageOriginalUrl));
}

interface Args {
  projectInstanceId?: string | null;
  sceneOriginId?: string | null;
  sceneInstanceId?: string | null;
  enabled: boolean;
}

interface AnchorState {
  category: AnchorCategory | null;
  imgUrl: string | null;
  sourceUrl: string | null;
  latitude: number | null;
  longitude: number | null;
}

const EMPTY_ANCHOR: AnchorState = { category: null, imgUrl: null, sourceUrl: null, latitude: null, longitude: null };

// The publish flows mount several instances of this hook at once (the project
// modal plus one per scene card). Each holds private state, so a save through
// one instance broadcasts to the others to keep their reads fresh.
type ImagesListener = () => void;
const imagesListeners = new Set<ImagesListener>();
function broadcastImagesChanged(except: ImagesListener | null) {
  for (const listener of imagesListeners) {
    if (listener !== except) listener();
  }
}

export function usePublishFlowImages({ projectInstanceId, sceneOriginId, sceneInstanceId, enabled }: Args): PublishFlowImagesApi {
  const services = useServices();
  const { user } = useAuthUser();
  const userId = user?.id;
  const [projectImages, setProjectImages] = useState<UIImage[]>([]);
  const [sceneOriginImages, setSceneOriginImages] = useState<UIImage[]>([]);
  const [sceneInstanceImages, setSceneInstanceImages] = useState<UIImage[]>([]);
  const [anchor, setAnchor] = useState<AnchorState>(EMPTY_ANCHOR);

  const refetch = useCallback(async () => Promise.all([
    projectInstanceId
      ? services.uiImages.getUIImages({ projectInstanceId })
      : Promise.resolve({ success: true, data: [] as UIImage[] }),
    sceneOriginId
      ? services.uiImages.getUIImagesForSceneOrigin(sceneOriginId, true)
      : Promise.resolve({ success: true, data: [] as UIImage[] }),
    sceneInstanceId
      ? services.uiImages.getUIImages({ sceneInstanceId })
      : Promise.resolve({ success: true, data: [] as UIImage[] }),
    sceneInstanceId
      ? listAll(page =>
          v1.GET('/api/v1/asset-instances', {
            params: {
              query: {
                sceneUniqueId: [sceneInstanceId],
                category: ['immersal', 'image_anchor', 'geospatial_anchor'],
                sort: 'createdAt',
                ...page,
              },
            },
          }),
        )
      : Promise.resolve({ data: null }),
  ]).then(([projectRes, sceneOriginRes, sceneInstanceRes, anchorRes]) => {
    setProjectImages(projectRes.success && projectRes.data ? projectRes.data : []);
    setSceneOriginImages(sceneOriginRes.success && sceneOriginRes.data ? sceneOriginRes.data : []);
    setSceneInstanceImages(sceneInstanceRes.success && sceneInstanceRes.data ? sceneInstanceRes.data : []);

    // The newest anchor wins; the list is in creation order, so it is the last one
    const newestAnchor = anchorRes.data?.at(-1);
    const anchorRow: Record<string, unknown> | undefined = newestAnchor ? assetInstanceToLegacy(newestAnchor) : undefined;
    if (sceneInstanceId && anchorRow) {
      const category = anchorRow.asset_instance_category as AnchorCategory;
      setAnchor({
        category,
        imgUrl: category === 'image_anchor' ? null : ((anchorRow.asset_instance_img_url as string) ?? null),
        sourceUrl: category === 'image_anchor' ? ((anchorRow.asset_instance_source_url as string) ?? null) : null,
        latitude: (anchorRow.asset_instance_latitude as number) ?? null,
        longitude: (anchorRow.asset_instance_longitude as number) ?? null,
      });
    } else {
      setAnchor(
        sceneInstanceId
          ? { category: 'place_anywhere', imgUrl: null, sourceUrl: null, latitude: null, longitude: null }
          : EMPTY_ANCHOR,
      );
    }
  }), [projectInstanceId, sceneOriginId, sceneInstanceId, services.uiImages]);

  // Disabling clears what the hook read, as does a change to what it reads while disabled
  const [readFor, setReadFor] = useState({ enabled, refetch });
  if (readFor.enabled !== enabled || readFor.refetch !== refetch) {
    setReadFor({ enabled, refetch });
    if (!enabled) {
      setProjectImages([]);
      setSceneOriginImages([]);
      setSceneInstanceImages([]);
      setAnchor(EMPTY_ANCHOR);
    }
  }

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void refetch().catch(() => {
      if (!cancelled) {
        setProjectImages([]);
        setSceneOriginImages([]);
        setSceneInstanceImages([]);
        setAnchor(EMPTY_ANCHOR);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, refetch]);

  const selfListener = useRef<ImagesListener | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const listener: ImagesListener = () => {
      void refetch().catch(() => {});
    };
    selfListener.current = listener;
    imagesListeners.add(listener);
    return () => {
      imagesListeners.delete(listener);
      selfListener.current = null;
    };
  }, [enabled, refetch]);

  const refetchAndBroadcast = useCallback(async () => {
    await refetch();
    broadcastImagesChanged(selfListener.current);
  }, [refetch]);

  const saveImage = useCallback(
    async (category: CoverCategory, payload: CoverSavePayload) => {
      if (!userId) {
        console.warn('[ai-covers] saveImage skipped: no user id', { category });
        return;
      }
      const isScene = category === 'SceneLandscape';
      const keyId = isScene ? sceneOriginId : projectInstanceId;
      if (!keyId) {
        console.warn('[ai-covers] saveImage skipped: missing key id', { category, isScene, sceneOriginId, projectInstanceId });
        return;
      }

      const existing = await services.uiImages.getUIImages({
        ...(isScene ? { sceneOriginId: keyId } : { projectInstanceId: keyId }),
        imageCategory: category,
      });
      // An empty object (not undefined) so replacing an image clears any crop
      // saved against the previous one.
      const metadata = payload.crop ? { crop: payload.crop } : {};

      // An unread list would make a second row of the category
      if (!existing.success) throw new Error(IMAGE_LIST_FAILED);
      // With more than one row of a category, update the same row the tiles display
      const target = pickDisplayedImage(existing.data ?? [], category);

      if (target) {
        console.log('[ai-covers] saveImage updating existing row', { category, imageId: target.imageId });
        const result = await services.uiImages.updateUIImage(target.imageId, {
          imageOriginalUrl: payload.imageOriginalUrl,
          imageCroppedUrl: payload.imageCroppedUrl,
          imageMetadata: metadata,
        });
        if (!result.success) {
          console.error('[ai-covers] saveImage update failed', { category, imageId: target.imageId, error: result.error });
          throw new Error(result.error ?? 'Image save failed');
        }
      } else {
        console.log('[ai-covers] saveImage creating new row', { category, projectInstanceId, sceneOriginId });
        const result = await services.uiImages.createUIImage({
          projectInstanceId: projectInstanceId ?? undefined,
          sceneOriginId: isScene ? sceneOriginId ?? undefined : undefined,
          imageCategory: category,
          imageOriginalUrl: payload.imageOriginalUrl,
          imageCroppedUrl: payload.imageCroppedUrl,
          userId,
          imageMetadata: metadata,
        });
        if (!result.success) {
          console.error('[ai-covers] saveImage create failed', { category, error: result.error });
          throw new Error(result.error ?? 'Image save failed');
        }
      }
      await refetchAndBroadcast();
      console.log('[ai-covers] saveImage done + refetched', { category });
    },
    [userId, projectInstanceId, sceneOriginId, services.uiImages, refetchAndBroadcast],
  );

  const removeImage = useCallback(
    async (category: CoverCategory) => {
      const isScene = category === 'SceneLandscape';
      const keyId = isScene ? sceneOriginId : projectInstanceId;
      if (!keyId) return;
      const existing = await services.uiImages.getUIImages({
        ...(isScene ? { sceneOriginId: keyId } : { projectInstanceId: keyId }),
        imageCategory: category,
      });
      const target = (existing.success ? existing.data ?? [] : [])
        .slice()
        .sort((a, b) => (a.imageOrderNumber || 0) - (b.imageOrderNumber || 0))[0];
      if (target) {
        await services.uiImages.deleteUIImage(target.imageId);
      }
      await refetchAndBroadcast();
    },
    [projectInstanceId, sceneOriginId, services.uiImages, refetchAndBroadcast],
  );

  // Updates the story's newest ScanImage, as the story page does, creating one only when the story has none
  const saveScanImage = useCallback(
    async (payload: CoverSavePayload) => {
      if (!userId || !sceneOriginId) return;
      // An empty object (not undefined) so replacing an image clears any crop
      // saved against the previous one.
      const metadata = payload.crop ? { crop: payload.crop } : {};
      const existing = await services.uiImages.getUIImages({ sceneOriginId, imageCategory: 'ScanImage' });
      if (!existing.success) throw new Error(existing.error ?? 'Scan image save failed');

      const result = existing.data && existing.data.length > 0
        ? await services.uiImages.updateUIImage(existing.data[0].imageId, {
          imageOriginalUrl: payload.imageOriginalUrl,
          imageCroppedUrl: payload.imageCroppedUrl,
          imageMetadata: metadata,
        })
        : await services.uiImages.createUIImage({
          sceneInstanceId: sceneInstanceId ?? undefined,
          sceneOriginId,
          projectInstanceId: projectInstanceId ?? undefined,
          imageCategory: 'ScanImage',
          imageOriginalUrl: payload.imageOriginalUrl,
          imageCroppedUrl: payload.imageCroppedUrl,
          userId,
          imageMetadata: metadata,
        });
      if (!result.success) throw new Error(result.error ?? 'Scan image save failed');
      await refetchAndBroadcast();
    },
    [userId, sceneInstanceId, sceneOriginId, projectInstanceId, services.uiImages, refetchAndBroadcast],
  );

  // Every version's, so no older scan image of the story takes its place
  const removeScanImage = useCallback(async () => {
    if (!sceneOriginId) return;
    const existing = await services.uiImages.getUIImages({ sceneOriginId, imageCategory: 'ScanImage' });
    for (const img of existing.data ?? []) {
      await services.uiImages.deleteUIImage(img.imageId);
    }
    await refetchAndBroadcast();
  }, [sceneOriginId, services.uiImages, refetchAndBroadcast]);

  // The video lives in image_original_url on a single SceneVideo row; there is no
  // cropped variant, which is why it doesn't go through saveImage.
  //
  // Like every other save here, this writes on upload rather than on submit, and to
  // the source story's origin. That is deliberate: it is what lets a creator close
  // the modal mid-flow and come back without re-uploading a 500MB video. Nothing
  // outside the template media step reads SceneVideo or TemplateLandscape, and the
  // gallery reads the copy's origin, so an abandoned upload stays invisible until
  // the creator returns to the step that shows it and requires it.
  const saveTemplateVideo = useCallback(
    async (videoUrl: string) => {
      if (!userId || !sceneOriginId) return;
      const existing = await services.uiImages.getUIImages({ sceneOriginId, imageCategory: 'SceneVideo' });
      const current = existing.success && existing.data?.length ? existing.data[0] : null;
      if (current) {
        await services.uiImages.updateUIImage(current.imageId, { imageOriginalUrl: videoUrl });
      } else {
        await services.uiImages.createUIImage({
          sceneOriginId,
          projectInstanceId: projectInstanceId ?? undefined,
          imageCategory: 'SceneVideo',
          imageOriginalUrl: videoUrl,
          userId,
        });
      }
      await refetchAndBroadcast();
    },
    [userId, sceneOriginId, projectInstanceId, services.uiImages, refetchAndBroadcast],
  );

  const removeTemplateVideo = useCallback(async () => {
    if (!sceneOriginId) return;
    const existing = await services.uiImages.getUIImages({ sceneOriginId, imageCategory: 'SceneVideo' });
    for (const img of existing.data ?? []) {
      await services.uiImages.deleteUIImage(img.imageId);
    }
    await refetchAndBroadcast();
  }, [sceneOriginId, services.uiImages, refetchAndBroadcast]);

  // The template's own cover. Leaving it unset is a supported state, not an empty
  // one: the gallery then reads the story's SceneLandscape, which is where every
  // template's cover came from before this row existed.
  const saveTemplateCover = useCallback(
    async (payload: CoverSavePayload) => {
      if (!userId || !sceneOriginId) return;
      // An empty object (not undefined) so replacing an image clears any crop
      // saved against the previous one.
      const metadata = payload.crop ? { crop: payload.crop } : {};
      const existing = await services.uiImages.getUIImages({
        sceneOriginId,
        imageCategory: 'TemplateLandscape',
      });
      const current = (existing.data ?? []).find(
        (img) => img.imageOrderNumber === TEMPLATE_COVER_ORDER,
      );
      if (current) {
        await services.uiImages.updateUIImage(current.imageId, {
          imageOriginalUrl: payload.imageOriginalUrl,
          imageCroppedUrl: payload.imageCroppedUrl,
          imageMetadata: metadata,
        });
      } else {
        await services.uiImages.createUIImage({
          sceneOriginId,
          projectInstanceId: projectInstanceId ?? undefined,
          imageCategory: 'TemplateLandscape',
          imageOriginalUrl: payload.imageOriginalUrl,
          imageCroppedUrl: payload.imageCroppedUrl,
          userId,
          imageOrderNumber: TEMPLATE_COVER_ORDER,
          imageMetadata: metadata,
        });
      }
      await refetchAndBroadcast();
    },
    [userId, sceneOriginId, projectInstanceId, services.uiImages, refetchAndBroadcast],
  );

  const removeTemplateCover = useCallback(async () => {
    if (!sceneOriginId) return;
    const existing = await services.uiImages.getUIImages({
      sceneOriginId,
      imageCategory: 'TemplateLandscape',
    });
    for (const img of (existing.data ?? []).filter(
      (i) => i.imageOrderNumber === TEMPLATE_COVER_ORDER,
    )) {
      await services.uiImages.deleteUIImage(img.imageId);
    }
    await refetchAndBroadcast();
  }, [sceneOriginId, services.uiImages, refetchAndBroadcast]);

  const saveTemplateLandscape = useCallback(
    async (imageId: string | null, payload: CoverSavePayload) => {
      if (!userId || !sceneOriginId) return;
      // An empty object (not undefined) so replacing an image clears any crop
      // saved against the previous one.
      const metadata = payload.crop ? { crop: payload.crop } : {};
      if (imageId) {
        await services.uiImages.updateUIImage(imageId, {
          imageOriginalUrl: payload.imageOriginalUrl,
          imageCroppedUrl: payload.imageCroppedUrl,
          imageMetadata: metadata,
        });
      } else {
        const existing = await services.uiImages.getUIImages({
          sceneOriginId,
          imageCategory: 'TemplateLandscape',
        });
        const maxOrder = existing.success && existing.data?.length
          ? Math.max(...existing.data.map((img) => img.imageOrderNumber || 0))
          : 0;
        await services.uiImages.createUIImage({
          sceneOriginId,
          projectInstanceId: projectInstanceId ?? undefined,
          imageCategory: 'TemplateLandscape',
          imageOriginalUrl: payload.imageOriginalUrl,
          imageCroppedUrl: payload.imageCroppedUrl,
          userId,
          imageOrderNumber: maxOrder + 1,
          imageMetadata: metadata,
        });
      }
      await refetchAndBroadcast();
    },
    [userId, sceneOriginId, projectInstanceId, services.uiImages, refetchAndBroadcast],
  );

  const removeTemplateLandscape = useCallback(
    async (imageId: string) => {
      await services.uiImages.deleteUIImage(imageId);
      await refetchAndBroadcast();
    },
    [services.uiImages, refetchAndBroadcast],
  );

  return useMemo<PublishFlowImagesApi>(() => {
    const scanRow = newestScan(sceneOriginImages);
    const scanRecord = toEntry(scanRow);
    // Prefer the ScanImage record; fall back to the live anchor thumbnail so the
    // image shows immediately, before seedScanImageFromAnchor has written the record.
    const scanImageUrl = scanRecord.url ?? anchor.imgUrl;
    const scanImage: CoverEntry = scanRecord.url
      ? scanRecord
      : { id: null, url: anchor.imgUrl, originalUrl: anchor.imgUrl, crop: null };
    // The creator hasn't replaced the auto-populated thumbnail while there's no
    // ScanImage record, or the newest one is still an anchor's thumbnail.
    const scanImageIsAutoThumbnail =
      !!scanImageUrl &&
      (!scanRow ||
        isAnchorThumbnail(
          { originalUrl: scanRow.imageOriginalUrl, croppedUrl: scanRow.imageCroppedUrl, metadata: scanRow.imageMetadata },
          new Set(anchor.imgUrl ? [anchor.imgUrl] : []),
        ));

    const templateVideo = firstOfCategory(sceneOriginImages, 'SceneVideo');
    const templateLandscapeRows = sceneOriginImages.filter(
      (img) => img.imageCategory === 'TemplateLandscape',
    );
    const ownCoverRow = templateLandscapeRows.find(
      (img) => img.imageOrderNumber === TEMPLATE_COVER_ORDER,
    );
    const templateCoverOwn: TemplateLandscapeEntry | null = ownCoverRow
      ? { ...toEntry(ownCoverRow), imageId: ownCoverRow.imageId }
      : null;
    const templateLandscapes: TemplateLandscapeEntry[] = templateLandscapeRows
      .filter((img) => img.imageOrderNumber !== TEMPLATE_COVER_ORDER)
      .sort((a, b) => (a.imageOrderNumber || 0) - (b.imageOrderNumber || 0))
      .map((img) => ({ ...toEntry(img), imageId: img.imageId }));

    const sceneLandscape = toEntry(firstOfCategory(sceneOriginImages, 'SceneLandscape'));

    return {
      anchorCategory: anchor.category,
      anchorLatitude: anchor.latitude,
      anchorLongitude: anchor.longitude,
      projectPortrait: toEntry(firstOfCategory(projectImages, 'ProjectPortrait')),
      projectLandscape: toEntry(firstOfCategory(projectImages, 'ProjectLandscape')),
      sceneLandscape,
      scanImage,
      scanImageUrl,
      scanImageIsAutoThumbnail,
      imageAnchorUrl:
        anchor.sourceUrl ??
        pickUrl(sceneInstanceImages, 'ImageAnchor') ??
        pickUrl(sceneOriginImages, 'ImageAnchor'),
      templateVideoUrl: templateVideo?.imageOriginalUrl ?? null,
      templateCover: templateCoverOwn ?? sceneLandscape,
      templateCoverOwn,
      templateLandscapes,
      saveImage,
      removeImage,
      saveScanImage,
      removeScanImage,
      saveTemplateVideo,
      removeTemplateVideo,
      saveTemplateCover,
      removeTemplateCover,
      saveTemplateLandscape,
      removeTemplateLandscape,
    };
  }, [
    projectImages,
    sceneOriginImages,
    sceneInstanceImages,
    anchor,
    saveImage,
    removeImage,
    saveScanImage,
    removeScanImage,
    saveTemplateVideo,
    removeTemplateVideo,
    saveTemplateCover,
    removeTemplateCover,
    saveTemplateLandscape,
    removeTemplateLandscape,
  ]);
}
